import { z } from 'zod';
import { AllPaymentProvidersEnum } from '../../common-tools/providers';
import { PaymentMethodEnum } from '../../status';
/**
 * PaymentStatusV4
 *
 * Transaction-level lifecycle status for v4 payments and refunds.
 * Stored in All_Payment_Transactions.statusV4 (the v4 source of truth);
 * the legacy `status` column receives a mapped ActionStatusEnum value in
 * the same write so legacy reporting keeps working.
 *
 * CREATED is transient: the insert transaction moves the row to
 * PENDING_PROVIDER before commit, so a committed row is never stuck
 * pre-provider without an owner.
 *
 * There is deliberately no stored UNKNOWN: ambiguity is stored as
 * REQUIRES_RECONCILIATION, which is owned and resolved by the sale-level
 * payment-status reconciler.
 */
export var PaymentStatusV4;
(function (PaymentStatusV4) {
    PaymentStatusV4["CREATED"] = "CREATED";
    PaymentStatusV4["PENDING_PROVIDER"] = "PENDING_PROVIDER";
    PaymentStatusV4["PROCESSING"] = "PROCESSING";
    PaymentStatusV4["AWAITING_LOCAL_UPDATE"] = "AWAITING_LOCAL_UPDATE";
    PaymentStatusV4["REQUIRES_CAPTURE"] = "REQUIRES_CAPTURE";
    PaymentStatusV4["REQUIRES_RECONCILIATION"] = "REQUIRES_RECONCILIATION";
    PaymentStatusV4["SUCCEEDED"] = "SUCCEEDED";
    PaymentStatusV4["FAILED"] = "FAILED";
    PaymentStatusV4["CANCELED"] = "CANCELED";
    PaymentStatusV4["PARTIALLY_REFUNDED"] = "PARTIALLY_REFUNDED";
    PaymentStatusV4["REFUNDED"] = "REFUNDED";
})(PaymentStatusV4 || (PaymentStatusV4 = {}));
export const PaymentStatusV4Schema = z.nativeEnum(PaymentStatusV4);
/**
 * Aggregate payment progress for a sale. This intentionally complements the
 * legacy SourceStatusEnum: saleStatus continues to carry refund/cancellation
 * compatibility states, while saleProgress reports the actionable state of
 * all payment legs together.
 */
export var SaleProgressV4;
(function (SaleProgressV4) {
    SaleProgressV4["UNPAID"] = "UNPAID";
    SaleProgressV4["PARTIALLY_PAID"] = "PARTIALLY_PAID";
    SaleProgressV4["PENDING"] = "PENDING";
    SaleProgressV4["PAID"] = "PAID";
    SaleProgressV4["FAILED"] = "FAILED";
    SaleProgressV4["NEEDS_REVIEW"] = "NEEDS_REVIEW";
})(SaleProgressV4 || (SaleProgressV4 = {}));
export const SaleProgressV4Schema = z.nativeEnum(SaleProgressV4);
/**
 * PaymentTransactionTypeV4
 *
 * Row kind in All_Payment_Transactions for v4. Refunds are separate child
 * rows (transactionType REFUND, parentTransactionId set) — never a mutation
 * of the parent payment row alone.
 */
export var PaymentTransactionTypeV4;
(function (PaymentTransactionTypeV4) {
    PaymentTransactionTypeV4["PAYMENT"] = "PAYMENT";
    PaymentTransactionTypeV4["REFUND"] = "REFUND";
})(PaymentTransactionTypeV4 || (PaymentTransactionTypeV4 = {}));
export const PaymentTransactionTypeV4Schema = z.nativeEnum(PaymentTransactionTypeV4);
/**
 * Terminal-routed providers supported by the v4 checkout flow — this gates
 * the terminal validation chain only (direct payment methods never name a
 * terminal or a provider adapter). Values are members of the shared
 * AllPaymentProvidersEnum — v4 does not introduce a parallel enum.
 */
export const V4_SUPPORTED_PROVIDERS = [
    AllPaymentProvidersEnum.STRIPE,
    AllPaymentProvidersEnum.BLOCKCHYP,
];
/**
 * Every provider value a v4 transaction row (and therefore a v4 response)
 * can carry: the terminal providers plus CASH, which direct payments
 * (CASH/DEBT/WIC methods) write for legacy parity.
 */
export const PaymentProviderV4Schema = z.enum([
    AllPaymentProvidersEnum.STRIPE,
    AllPaymentProvidersEnum.BLOCKCHYP,
    AllPaymentProvidersEnum.CASH,
]);
/**
 * Payment methods settled directly by the store with no provider adapter,
 * terminal, or polling: the canonical row is created and transitioned to
 * SUCCEEDED synchronously inside the insert transaction. EBT_CASH is
 * intentionally absent (legacy folds it into cash; add it here if v4 ever
 * needs it as a distinct method).
 */
export const V4_DIRECT_PAYMENT_METHODS = [
    PaymentMethodEnum.CASH,
    PaymentMethodEnum.DEBT,
    PaymentMethodEnum.WIC,
];
/**
 * Payment methods executed online (card-not-present): the backend creates a
 * provider intent and returns its client credentials; the client confirms
 * with the provider SDK and the sale-level reconciler finalizes against
 * provider truth. No terminal, no executionMode.
 */
export const V4_ONLINE_PAYMENT_METHODS = [
    PaymentMethodEnum.MOBILE_APP_PAYMENT,
    PaymentMethodEnum.MANUAL_CARD,
    PaymentMethodEnum.WEB_PAYMENT,
    PaymentMethodEnum.PAYMENT_LINK,
];
/**
 * Providers able to serve the online (intent-based) path — the provider must
 * mint a client-confirmable payment intent. Stripe only for now; BlockChyp
 * has no equivalent primitive.
 */
export const V4_ONLINE_SUPPORTED_PROVIDERS = [
    AllPaymentProvidersEnum.STRIPE,
];
/** Request-level provider choice for the online branch (explicit, required). */
export const OnlineProviderV4Schema = z.enum([AllPaymentProvidersEnum.STRIPE]);
/**
 * Provider values that have no v4 provider adapter. v4 writes provider CASH
 * for every direct method (legacy parity — legacy records DEBT/WIC under
 * provider CASH too); DEBT is included defensively so reconciler/cancel/
 * local-update guards hold even if debt rows are ever split onto it.
 */
export const V4_NON_PROVIDER_PROVIDERS = [
    AllPaymentProvidersEnum.CASH,
    AllPaymentProvidersEnum.DEBT,
];
/**
 * Outcome reported by the frontend for a LOCAL_NETWORK payment attempt via
 * POST /v4/payment/local-update. UNKNOWN is a report value only — it is
 * stored as REQUIRES_RECONCILIATION, never as a status of its own.
 */
export const LocalPaymentOutcomeV4Schema = z.enum([
    'SUCCEEDED',
    'FAILED',
    'CANCELED',
    'UNKNOWN',
]);
/**
 * How a failed v4 payment may be retried, as advertised by the sale-level
 * payment-status endpoint after provider investigation.
 *
 * REUSE_PAYMENT_INTENT (Stripe only): call POST /v4/payment with a NEW
 * idempotencyKey plus retryOfTransactionId = the failed transaction id; the
 * new canonical transaction inherits and re-dispatches the same
 * PaymentIntent.
 */
export const RetryModeV4Schema = z.enum([
    'NEW_TRANSACTION',
    'REUSE_PAYMENT_INTENT',
]);
/** Current operating state of a physical payment terminal. */
export const TerminalStatusV4Schema = z.enum(['BUSY', 'AVAILABLE', 'UNKNOWN']);
